import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-sm border-b border-white/10 w-full">
      <div className="w-full px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 w-full">
          <Link to="/" className="text-lg sm:text-xl lg:text-2xl font-bold text-white flex-shrink-0" onClick={closeMobileMenu}>
            Your<span className="text-purple-400">Brand</span>Value
          </Link>
          
          <div className="hidden md:flex space-x-4 lg:space-x-8 flex-shrink-0">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">Inicio</Link>
            <Link to="/sobre-mi" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">Sobre Mí</Link>
            <Link to="/metodologia" className="text-purple-400 hover:text-purple-300 transition-colors text-sm lg:text-base font-semibold">Metodología</Link>
            <Link to="/casos-estudio" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">Casos</Link>
            <Link to="/asesoria" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">Asesoría</Link>
            <Link to="/contacto" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">Contacto</Link>
          </div>

          <div className="md:hidden flex-shrink-0">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-300 hover:text-white p-2 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-800/95 backdrop-blur-sm border-t border-white/10">
            <div className="px-4 pt-2 pb-3 space-y-1">
              <Link to="/" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>Inicio</Link>
              <Link to="/sobre-mi" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>Sobre Mí</Link>
              <Link to="/metodologia" className="block text-purple-400 hover:text-purple-300 py-2 transition-colors font-semibold" onClick={closeMobileMenu}>Metodología</Link>
              <Link to="/casos-estudio" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>Casos</Link>
              <Link to="/asesoria" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>Asesoría</Link>
              <Link to="/contacto" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>Contacto</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const MetodologiaPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const metodologySteps = [
    {
      number: "01",
      title: "Descubrimiento",
      description: "Análisis profundo de tu marca, mercado y audiencia",
      icon: "🔍",
      activities: [
        "Auditoria completa de tu presencia actual",
        "Análisis de competencia directa e indirecta", 
        "Investigación de audiencia y buyer personas",
        "Identificación de fortalezas y oportunidades"
      ],
      duration: "1-2 semanas"
    },
    {
      number: "02", 
      title: "Estrategia",
      description: "Definición de propósito, posicionamiento y diferenciación",
      icon: "🎯",
      activities: [
        "Definición de propósito y valores de marca",
        "Desarrollo de mensaje central y tono de voz",
        "Estrategia de posicionamiento diferencial",
        "Roadmap de implementación personalizado"
      ],
      duration: "2-3 semanas"
    },
    {
      number: "03",
      title: "Implementación", 
      description: "Ejecución coordinada en todos los puntos de contacto",
      icon: "🚀",
      activities: [
        "Optimización de presencia digital",
        "Creación de contenido estratégico",
        "Lanzamiento de campañas humanizadas",
        "Activación en canales prioritarios"
      ],
      duration: "4-8 semanas"
    },
    {
      number: "04",
      title: "Optimización",
      description: "Análisis de resultados y mejora continua",
      icon: "📊",
      activities: [
        "Monitoreo de KPIs y métricas clave",
        "Análisis de rendimiento por canal",
        "Optimización basada en datos",
        "Reportes y recomendaciones mensuales"
      ],
      duration: "Continuo"
    }
  ];

  const principles = [
    {
      title: "Las marcas no se inventan, se descubren",
      description: "Cada empresa tiene una esencia única que debe ser revelada, no creada artificialmente.",
      icon: "💎",
      color: "from-purple-600/20 to-pink-600/20"
    },
    {
      title: "Conectar antes de convencer", 
      description: "Una estrategia efectiva genera conexión emocional antes de buscar la conversión.",
      icon: "❤️",
      color: "from-blue-600/20 to-cyan-600/20"
    },
    {
      title: "Crecer sin dirección es solo ruido",
      description: "El crecimiento sostenible requiere estrategia, propósito y coherencia en cada acción.",
      icon: "🧭",
      color: "from-green-600/20 to-emerald-600/20"
    },
    {
      title: "El marketing humanizado transforma",
      description: "Respetar y valorar a las personas genera lealtad y resultados duraderos.",
      icon: "🌱",
      color: "from-orange-600/20 to-red-600/20"
    }
  ];

  const tools = [
    { name: "Brand Audit Canvas", description: "Análisis 360° de posición actual", icon: "📋" },
    { name: "Competitive Intelligence", description: "Mapeo detallado de competencia", icon: "🔍" },
    { name: "Persona Development", description: "Definición precisa de audiencias", icon: "👥" },
    { name: "Message Architecture", description: "Estructura de comunicación coherente", icon: "💬" },
    { name: "Content Strategy Matrix", description: "Planificación de contenido estratégico", icon: "📝" },
    { name: "Performance Analytics", description: "Métricas y KPIs personalizados", icon: "📊" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      
      {/* Header */}
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
          >
            <span className="mr-2">←</span>
            Volver al Home
          </Link>

          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-white mb-6">
              Mi <span className="text-purple-400">Metodología</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Un enfoque probado que mezcla estrategia, creatividad y propósito para construir marcas que perduran
            </p>
          </div>
        </div>
      </section>

      {/* Principios Fundamentales */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Mis Principios Fundamentales</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Estas creencias guían cada estrategia que desarrollo
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((principle, index) => (
              <div
                key={index}
                className={`bg-gradient-to-br ${principle.color} backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8 hover:border-purple-500/40 transition-all duration-300`}
              >
                <div className="text-center mb-6">
                  <div className="text-6xl mb-4">{principle.icon}</div>
                  <h3 className="text-2xl font-bold text-white mb-4">{principle.title}</h3>
                </div>
                <p className="text-gray-300 leading-relaxed text-center">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proceso de 4 Pasos */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Proceso de Transformación</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Mi metodología probada de 4 pasos para descubrir y potenciar tu marca
            </p>
          </div>

          <div className="space-y-12">
            {metodologySteps.map((step, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-6">
                        {step.number}
                      </div>
                      <div>
                        <h3 className="text-3xl font-bold text-white mb-2">{step.title}</h3>
                        <p className="text-purple-300">{step.description}</p>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {step.activities.map((activity, idx) => (
                        <li key={idx} className="flex items-start text-gray-300">
                          <span className="text-green-400 mr-3 mt-1 flex-shrink-0">✓</span>
                          <span className="text-sm leading-relaxed">{activity}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex items-center text-sm text-gray-400">
                      <span className="mr-2">⏱️</span>
                      <span>Duración: {step.duration}</span>
                    </div>
                  </div>
                </div>

                <div className={`text-center ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="text-9xl mb-4">{step.icon}</div>
                  <div className="bg-gradient-to-r from-purple-600/10 to-pink-600/10 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-6">
                    <h4 className="text-xl font-bold text-white mb-2">Paso {step.number}</h4>
                    <p className="text-gray-300 text-sm">
                      {step.number === "01" && "Fundación sólida para toda la estrategia"}
                      {step.number === "02" && "Claridad y dirección estratégica definida"} 
                      {step.number === "03" && "Activación coherente en todos los canales"}
                      {step.number === "04" && "Crecimiento sostenible y medible"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Herramientas y Frameworks */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Herramientas & Frameworks</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Metodologías propias desarrolladas durante 13+ años de experiencia
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tools.map((tool, index) => (
              <div
                key={index}
                className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-6 hover:border-purple-500/40 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="text-center mb-4">
                  <div className="text-4xl mb-3">{tool.icon}</div>
                  <h3 className="text-lg font-bold text-white mb-2">{tool.name}</h3>
                  <p className="text-gray-300 text-sm">{tool.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Resultados Típicos */}
          <div className="mt-16 bg-gradient-to-r from-green-600/20 to-emerald-600/20 backdrop-blur-sm rounded-2xl border border-green-500/30 p-8">
            <h3 className="text-2xl font-bold text-white mb-8 text-center">Resultados Típicos</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400 mb-2">150%</div>
                <div className="text-sm text-gray-300">Incremento engagement promedio</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400 mb-2">85%</div>
                <div className="text-sm text-gray-300">Mejora en reconocimiento</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400 mb-2">75%</div>
                <div className="text-sm text-gray-300">Crecimiento leads cualificados</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400 mb-2">6-12</div>
                <div className="text-sm text-gray-300">Meses impacto visible</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-12">
            <h2 className="text-3xl font-bold text-white mb-4">¿Lista tu marca para la transformación?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Apliquemos esta metodología probada a tu proyecto específico
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/asesoria"
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Ver Servicios de Asesoría
              </Link>
              <Link
                to="/contacto"
                className="border-2 border-purple-500 text-purple-400 px-8 py-4 rounded-xl font-bold text-lg hover:bg-purple-500 hover:text-white transition-all duration-300"
              >
                Consulta Personalizada
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MetodologiaPage;